import type {
	CompetitiveDataPointModel,
	PriceHistoryDataPointModel,
} from '@/shared/generated'
import type {
	ChartSerie,
	CompetitiveDataPointExtended,
	CompetitorDataGroup,
} from '../model/types'

import { setHours } from 'date-fns'
import { first, fromPairs, last, orderBy, prop } from 'lodash/fp'

import {
	colorForTopic,
	durations,
	normalizeDate,
} from '@/shared/components/Chart/utils'

export const DEFAULT_SERIE_COLOR = '#6B7176'
export const MY_SERIE_NAME = 'My price'
const LIMIT_TO_SHOW_SERIES = 7

export const createSerie = (
	name: string,
	color: string | null,
	data: [number, number | null][]
) => ({
	type: 'line',
	name,
	color: color || DEFAULT_SERIE_COLOR,
	pointInterval: durations.day,
	data: data,
})

/**
 * Get last available data from grouped datasets
 * @param {Array<Object>} objects  Serverside data
 * @returns {Array}
 */
export const pickLastData = (
	objects: CompetitorDataGroup[]
): CompetitiveDataPointExtended[] =>
	objects.map(
		(data) =>
			({
				...(first(
					orderBy(['observed_on'], ['desc'], data.objects)
				) as CompetitiveDataPointModel),
				label: data.competitor_name,
				color: data.color,
			}) as CompetitiveDataPointExtended
	)

/**
 *
 * @param {Array} data  Serverside data
 * @param {Number} interval  milseconds
 *
 * @returns {Array}  with normalized date and values [date,value]
 */
export const prepareSerieDataset = (
	data?: {
		observed_on: string
		price: number
	}[],
	interval = durations.day
): [number, number][] =>
	data?.map((d) => [
		normalizeDate(Number(setHours(new Date(d.observed_on), 3)), interval),
		+d.price,
	]) || []

/**
 *
 * @param {Array} list Serverside data
 *
 * @returns {Array} the same array with color
 */
export const enrichWithColors = (
	list: {
		competitor_name?: string
	}[],
	colors: Record<string, string> = {}
): CompetitorDataGroup[] =>
	list.map((data) => ({
		...data,
		color: data?.competitor_name ? colors[data?.competitor_name] : '',
	}))

/**
 * Allign all series to common dataset length
 *
 * @param {Array} series
 * @param {Number} interval
 */
export const alignDatasets = (
	series: ChartSerie[],
	interval = durations.day
): ChartSerie[] => {
	const end = normalizeDate(Number(new Date()), interval) * interval
	const start = end - durations.week * 6

	return series.map((serie) => {
		const dataMap = fromPairs(serie.data)
		const ds = createDataset(start, end)

		return {
			...serie,
			data: ds.map(([date]) => [date * interval, dataMap[date] || null]),
		}
	})
}

/**
 * Create new dataset with Dates and null values
 *
 * @param {Number} from
 * @param {Number} to
 * @param {Number} interval
 *
 * @returns {Array} array with normalized detes and empty values
 */
export const createDataset = (
	from: number,
	to: number,
	interval = durations.day
) => {
	const set: [number, null][] = []
	for (let i = from; i <= to; i += interval) {
		set.push([normalizeDate(i, interval), null])
	}

	return set
}
export const createColorMap = (
	list: {
		competitor_name?: string
		objects?: CompetitiveDataPointModel[]
	}[]
): Record<string, string> =>
	fromPairs(
		list.map(({ competitor_name }, index) => [
			competitor_name,
			colorForTopic(list.length, index),
		])
	)

const createMyData = (
	data: PriceHistoryDataPointModel[]
): CompetitiveDataPointExtended =>
	Object.assign({}, last<PriceHistoryDataPointModel>(data), {
		label: MY_SERIE_NAME,
		color: DEFAULT_SERIE_COLOR,
	}) as CompetitiveDataPointExtended

const createVisibleSeries = (
	dataset: CompetitorDataGroup[],
	competitors: string[]
): string[] =>
	dataset
		.filter(({ competitor_name }) => competitors.includes(competitor_name!))
		.map(prop('competitor_name') as (g: CompetitorDataGroup) => string)
		.splice(0, LIMIT_TO_SHOW_SERIES - 1)
		.concat([MY_SERIE_NAME])

/** Derives chart series, last-price rows and default visible series names. */
export const buildMarketView = (
	myData: PriceHistoryDataPointModel[],
	historyGroups: { competitor_name?: string }[],
	marketGroups: { competitor_name?: string }[],
	ruleCompetitors: string[]
) => {
	const colors = createColorMap(marketGroups)
	const market = enrichWithColors(marketGroups, colors)
	const history = enrichWithColors(historyGroups, colors)

	return {
		chartSeries: [
			createSerie(
				MY_SERIE_NAME,
				DEFAULT_SERIE_COLOR,
				prepareSerieDataset(myData)
			),
			...history.map(({ competitor_name, color, objects }) =>
				createSerie(competitor_name!, color!, prepareSerieDataset(objects))
			),
		],
		marketPrices: [createMyData(myData), ...pickLastData(market)],
		defaultVisible: createVisibleSeries(market, ruleCompetitors),
	}
}
